import { message } from 'antd';

import { PayloadAction } from '@reduxjs/toolkit';

import { getEnvBaseUrl } from '../../../../shared/common/helpers';
import { Axios } from '../../../../shared/common/http';
import { AppDispatch, AppThunk, RootState } from '../../../../shared/store';
import {
    CreateDsiInterventionRequestDto,
    CreateDsiRuleRequestDto,
    DsiApiEnvelope,
    DsiInterventionDto,
    DsiRuleDto,
    DsiRuleVocabularyDto,
    UpdateDsiInterventionRequestDto,
    UpdateDsiRuleRequestDto,
} from './components/dsi-interventions/types';
import { AdminState } from './models';

// Action-type constants resolved statically so the thunks below don't depend on
// adminSlice.actions resolution order at module load (matches slice.cqm-measure-configuration.ts).
const SET_INTERVENTIONS = 'admin/setDsiInterventions';
const SYNC_INTERVENTION = 'admin/syncDsiIntervention';
const SET_ACCESS_DENIED = 'admin/setDsiInterventionsAccessDenied';
const SET_SAVE_ERROR = 'admin/setDsiInterventionsSaveError';
const SET_ERROR = 'admin/setError';
const SET_RULES = 'admin/setDsiRules';
const SET_RULE_VOCABULARY = 'admin/setDsiRuleVocabulary';
const SET_RULES_ERROR = 'admin/setDsiRulesError';

// Hand-rolled against the shared Axios: the pinned @medone/medonehp-api-client predates
// DsiConfigurationController (B11-S1, PR #407). See types.ts for the TODO.
const interventionsUrl = () => `${getEnvBaseUrl()}/api/dsi/interventions`;
const rulesUrl = (key: string) => `${interventionsUrl()}/${encodeURIComponent(key)}/rules`;
const ruleVocabularyUrl = () => `${getEnvBaseUrl()}/api/dsi/rules/vocabulary`;

export const ACCESS_DENIED_MESSAGE = 'Access denied. DSI configuration is switched off for this environment or requires the System Admin role.';
export const CONFLICT_MESSAGE = 'The intervention was modified by another user. Refresh and try again.';
const LOAD_FAILED_MESSAGE = 'Failed to load DSI interventions.';
const SAVE_FAILED_MESSAGE = 'Failed to save the DSI intervention.';
const CREATE_FAILED_MESSAGE = 'The predictive intervention was not created.';
export const RULE_CONFLICT_MESSAGE = 'The rule was modified by another user. The list was reloaded; review it and try again.';
const RULES_LOAD_FAILED_MESSAGE = 'Failed to load the trigger rules.';
const RULE_SAVE_FAILED_MESSAGE = 'The trigger rule was not saved.';
const RULE_DELETE_FAILED_MESSAGE = 'The trigger rule was not deleted.';

export const reducers = {
    setDsiInterventions: (state: AdminState, action: PayloadAction<DsiInterventionDto[]>) => {
        state.dsiInterventions = action.payload;
        state.dsiInterventionsAccessDenied = false;
        state.errorMessage = null;
    },
    syncDsiIntervention: (state: AdminState, action: PayloadAction<DsiInterventionDto>) => {
        const items = state.dsiInterventions ?? [];
        const index = items.findIndex((x) => x.key === action.payload.key);

        state.dsiInterventions = index === -1 ? [...items, action.payload] : items.map((item, i) => (i === index ? action.payload : item));
        state.errorMessage = null;
        state.dsiInterventionsSaveError = null;
    },
    setDsiInterventionsAccessDenied: (state: AdminState, action: PayloadAction<boolean>) => {
        state.dsiInterventionsAccessDenied = action.payload;
    },
    setDsiInterventionsSaveError: (state: AdminState, action: PayloadAction<string | null>) => {
        state.dsiInterventionsSaveError = action.payload;
    },
    setDsiRules: (state: AdminState, action: PayloadAction<{ key: string; rules: DsiRuleDto[] }>) => {
        state.dsiRules = { ...(state.dsiRules ?? {}), [action.payload.key]: action.payload.rules };
    },
    setDsiRuleVocabulary: (state: AdminState, action: PayloadAction<DsiRuleVocabularyDto>) => {
        state.dsiRuleVocabulary = action.payload;
    },
    setDsiRulesError: (state: AdminState, action: PayloadAction<string | null>) => {
        state.dsiRulesError = action.payload;
    },
};

export const clearDsiInterventionsSaveError = () => ({ type: SET_SAVE_ERROR, payload: null });

export const clearDsiRulesError = () => ({ type: SET_RULES_ERROR, payload: null });

// The backend answers with ProblemDetails (403 flag off / no claim, 409 stale RowVersion, 400 validation).
// Only `detail`/`title` ever reach the UI — never `fullException` (information disclosure).
const describeFailure = (error: unknown): { status?: number; detail?: string } => {
    const response = (error as { response?: { status?: number; data?: unknown } } | null)?.response;
    const body = (response?.data ?? null) as { title?: string; detail?: string; message?: string } | null;
    const detail = body?.detail || body?.message || body?.title || undefined;

    return { status: response?.status, detail };
};

const reportFailure = (dispatch: AppDispatch, error: unknown, fallback: string, isSave = false): { status?: number } => {
    const { status, detail } = describeFailure(error);
    let shown: string;

    if (status === 403) {
        dispatch({ type: SET_ACCESS_DENIED, payload: true });
        shown = ACCESS_DENIED_MESSAGE;
    } else if (status === 409) {
        shown = detail ?? CONFLICT_MESSAGE;
    } else {
        shown = detail ?? fallback;
    }

    message.error(shown);
    dispatch({ type: SET_ERROR, payload: detail ?? fallback });

    // A failed save also lands in the drawer, next to the fields the API rejected (400) or that went stale (409).
    if (isSave) {
        dispatch({ type: SET_SAVE_ERROR, payload: shown });
    }

    return { status };
};

export function fetchDsiInterventions(): AppThunk {
    return async (dispatch: AppDispatch) => {
        try {
            const response = await Axios.get<DsiApiEnvelope<DsiInterventionDto[]>>(interventionsUrl());

            if (response.data?.succeeded) {
                dispatch({ type: SET_INTERVENTIONS, payload: response.data.entity ?? [] });
            } else {
                reportFailure(dispatch, null, response.data?.message || LOAD_FAILED_MESSAGE);
            }
        } catch (error) {
            reportFailure(dispatch, error, LOAD_FAILED_MESSAGE);
        }
    };
}

export function createPredictiveDsiIntervention(request: CreateDsiInterventionRequestDto): AppThunk<Promise<boolean>> {
    return async (dispatch: AppDispatch) => {
        try {
            const response = await Axios.post<DsiApiEnvelope<DsiInterventionDto>>(interventionsUrl(), request);

            if (response.data?.succeeded && response.data.entity) {
                // Reload rather than append: the table is sorted by name server-side, and a local
                // append would put the new row in the wrong place until the next fetch.
                await dispatch(fetchDsiInterventions());

                return true;
            }

            reportFailure(dispatch, null, response.data?.message || CREATE_FAILED_MESSAGE, true);
        } catch (error) {
            reportFailure(dispatch, error, CREATE_FAILED_MESSAGE, true);
        }

        return false;
    };
}

export function updateDsiIntervention(key: string, request: UpdateDsiInterventionRequestDto): AppThunk<Promise<boolean>> {
    return async (dispatch: AppDispatch) => {
        try {
            const response = await Axios.put<DsiApiEnvelope<DsiInterventionDto>>(`${interventionsUrl()}/${encodeURIComponent(key)}`, request);

            if (response.data?.succeeded && response.data.entity) {
                dispatch({ type: SYNC_INTERVENTION, payload: response.data.entity });

                return true;
            }

            reportFailure(dispatch, null, response.data?.message || SAVE_FAILED_MESSAGE, true);
        } catch (error) {
            const { status } = reportFailure(dispatch, error, SAVE_FAILED_MESSAGE, true);

            // A stale RowVersion means our copy is behind: reload so the next edit starts from the current row.
            if (status === 409) {
                await dispatch(fetchDsiInterventions());
            }
        }

        return false;
    };
}

// ---- SCRUM-1367 trigger rules: the Rules tab of the drawer ----

// Rule failures stay inside the tab, next to the rule that failed, rather than in the page-level error:
// a 400 names the exact predicate problem and the administrator needs it where they are editing.
const reportRuleFailure = (dispatch: AppDispatch, error: unknown, fallback: string): { status?: number } => {
    const { status, detail } = describeFailure(error);
    let shown: string;

    if (status === 403) {
        dispatch({ type: SET_ACCESS_DENIED, payload: true });
        shown = ACCESS_DENIED_MESSAGE;
    } else if (status === 409) {
        shown = RULE_CONFLICT_MESSAGE;
    } else {
        shown = detail ?? fallback;
    }

    message.error(shown);
    dispatch({ type: SET_RULES_ERROR, payload: shown });

    return { status };
};

export function fetchDsiRuleVocabulary(): AppThunk {
    return async (dispatch: AppDispatch) => {
        try {
            const response = await Axios.get<DsiApiEnvelope<DsiRuleVocabularyDto>>(ruleVocabularyUrl());

            if (response.data?.succeeded && response.data.entity) {
                dispatch({ type: SET_RULE_VOCABULARY, payload: response.data.entity });
            }
        } catch (error) {
            reportRuleFailure(dispatch, error, RULES_LOAD_FAILED_MESSAGE);
        }
    };
}

export function fetchDsiRules(key: string): AppThunk<Promise<void>> {
    return async (dispatch: AppDispatch) => {
        try {
            const response = await Axios.get<DsiApiEnvelope<DsiRuleDto[]>>(rulesUrl(key));

            if (response.data?.succeeded) {
                dispatch({ type: SET_RULES, payload: { key, rules: response.data.entity ?? [] } });
            } else {
                reportRuleFailure(dispatch, null, response.data?.message || RULES_LOAD_FAILED_MESSAGE);
            }
        } catch (error) {
            reportRuleFailure(dispatch, error, RULES_LOAD_FAILED_MESSAGE);
        }
    };
}

// Reloading after every write keeps the list in the server's order (by name) and hands back the
// fresh RowVersions the next edit must echo.
const ruleWrite = (key: string, write: () => Promise<{ data?: DsiApiEnvelope<DsiRuleDto> }>, fallback: string): AppThunk<Promise<boolean>> => {
    return async (dispatch: AppDispatch) => {
        dispatch({ type: SET_RULES_ERROR, payload: null });

        try {
            const response = await write();

            if (response.data?.succeeded) {
                await dispatch(fetchDsiRules(key));

                return true;
            }

            reportRuleFailure(dispatch, null, response.data?.message || fallback);
        } catch (error) {
            const { status } = reportRuleFailure(dispatch, error, fallback);

            if (status === 409) {
                await dispatch(fetchDsiRules(key));
            }
        }

        return false;
    };
};

export const createDsiRule = (key: string, request: CreateDsiRuleRequestDto): AppThunk<Promise<boolean>> =>
    ruleWrite(key, () => Axios.post<DsiApiEnvelope<DsiRuleDto>>(rulesUrl(key), request), RULE_SAVE_FAILED_MESSAGE);

export const updateDsiRule = (key: string, ruleId: number, request: UpdateDsiRuleRequestDto): AppThunk<Promise<boolean>> =>
    ruleWrite(key, () => Axios.put<DsiApiEnvelope<DsiRuleDto>>(`${rulesUrl(key)}/${ruleId}`, request), RULE_SAVE_FAILED_MESSAGE);

export const deleteDsiRule = (key: string, ruleId: number, rowVersion: string | null | undefined): AppThunk<Promise<boolean>> =>
    ruleWrite(key, () => Axios.delete<DsiApiEnvelope<DsiRuleDto>>(`${rulesUrl(key)}/${ruleId}`, { data: { rowVersion: rowVersion ?? null } }), RULE_DELETE_FAILED_MESSAGE);

export const selectDsiRules = (key: string | null | undefined) => (state: RootState) => (key ? state.admin.dsiRules?.[key] : undefined);

export const selectDsiRuleVocabulary = (state: RootState) => state.admin.dsiRuleVocabulary;

export const selectDsiRulesError = (state: RootState) => state.admin.dsiRulesError;

export const selectDsiInterventions = (state: RootState) => state.admin.dsiInterventions;

export const selectDsiInterventionsAccessDenied = (state: RootState) => state.admin.dsiInterventionsAccessDenied;

export const selectDsiInterventionsSaveError = (state: RootState) => state.admin.dsiInterventionsSaveError;
